import KNFormCheckboxGroup from 'components/KNForm/KNFormCheckboxGroup'
import { type ReactElement, useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { StopLegPair } from 'screens/TripDetails/TripDetails.types'

export interface ShipmentNumbersFieldProps {
  stopLegPairs: StopLegPair[]
  availableStopLegPairs: StopLegPair[]
  bulkStatusUpdateOnly?: boolean
}

const getLabel = (stopLegPair: StopLegPair) => {
  const assetId = stopLegPair.leg.wayPoints
    .find((stop) => stop.type === stopLegPair.stop.type)
    ?.references
    ?.find((reference) => reference.code === 'HAS')
    ?.value
  if (assetId) {
    return `${stopLegPair.leg.shipmentNumber} / ${assetId}`
  }
  return stopLegPair.leg.shipmentNumber
}

const ShipmentNumbersField = ({
  stopLegPairs,
  availableStopLegPairs,
  bulkStatusUpdateOnly,
}: ShipmentNumbersFieldProps): ReactElement => {
  const { t } = useTranslation()
  const { watch, setValue } = useFormContext()
  const [availableShipmentNumbers, setAvailableShipmentNumbers] = useState<string[]>([])
  const watchedShipmentNumbers = watch('shipmentNumbers')

  useEffect(() => {
    const availableShipmentNumbers = availableStopLegPairs.map((stopLegPair) => stopLegPair.leg.shipmentNumber)
    setAvailableShipmentNumbers(availableShipmentNumbers)
    const selectedShipmentNumbers = bulkStatusUpdateOnly ? availableShipmentNumbers : []
    setValue('shipmentNumbers', selectedShipmentNumbers)
  }, [stopLegPairs, availableStopLegPairs])

  const isOptionDisabled = useCallback((shipmentNumber: string) => {
    return bulkStatusUpdateOnly // disabled if selected status is bulk only
      || availableShipmentNumbers.length === 0 // disabled if no status is selected
      || !availableShipmentNumbers.includes(shipmentNumber) // disabled if shipment is not related to selected status
  }, [bulkStatusUpdateOnly, availableShipmentNumbers])

  return (
    <KNFormCheckboxGroup
      name="shipmentNumbers"
      label={t('screens.cs.trip_details.update_status.shipments_count', {
        count: watchedShipmentNumbers.length,
        total: stopLegPairs.length,
      })}
      options={stopLegPairs.map((stopLegPair) => ({
        value: stopLegPair.leg.shipmentNumber,
        label: getLabel(stopLegPair),
        disabled: isOptionDisabled(stopLegPair.leg.shipmentNumber),
      }))}
      rules={{
        required: t('form.validation.shipment_required'),
      }}
      variant="outlined"
    />
  )
}

export default ShipmentNumbersField
